/// <amd-module name="Repos/Views/PullRequestListingPageActions/Components/CustomizeQueryPanel" />
import * as React from "react";
import { PullRequestStatus } from "Repos/Common/Contracts/Git";
import { branchNameToRef, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IPullRequestQuery } from "Repos/PullRequestList/Contracts";
import { VersionDropdown, VersionDropdownExpandableTextField } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import * as Resources from "Repos/Views/PullRequestListingPageActions/Resources";
import { IReadonlyObservableValue, ObservableArray, ObservableValue, useObservable } from "VSS/Core/Observable";
import { IPeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { useComponentContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader, PanelOverlay } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { arrayEquals } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface ICustomizeQueryPanelProps {
    errorMessage: IReadonlyObservableValue<string>;
    initialQuery: IPullRequestQuery;
    myTeamIds: string[];
    onDismiss: () => void;
    onSave: (query: IPullRequestQuery) => void;
    showOverlay: IReadonlyObservableValue<boolean>;
    usersAndGroupsProvider: IPeoplePickerProvider;
    usersOnlyProvider: IPeoplePickerProvider;
}

function pullRequestStatusToString(status: PullRequestStatus) {
    switch (status) {
        case PullRequestStatus.Abandoned:
            return Resources.Abandoned;
        case PullRequestStatus.All:
            return Resources.All;
        case PullRequestStatus.Completed:
            return Resources.Completed;
        default:
            return Resources.Active;
    }
}

function getListBoxItem(status: PullRequestStatus): IListBoxItem<PullRequestStatus> {
    return { data: status, id: status.toString(), text: pullRequestStatusToString(status) };
}

const statusItems: IListBoxItem<PullRequestStatus>[] = [
    getListBoxItem(PullRequestStatus.All),
    getListBoxItem(PullRequestStatus.Active),
    getListBoxItem(PullRequestStatus.Completed),
    getListBoxItem(PullRequestStatus.Abandoned)
];

const draftStateItems: IListBoxItem<boolean | undefined>[] = [
    { data: undefined, id: "any", text: Resources.DraftStateAny },
    { data: false, id: "noDrafts", text: Resources.DraftStateNoDrafts },
    { data: true, id: "onlyDrafts", text: Resources.DraftStateOnlyDrafts }
];

export const CustomizeQueryPanel = (props: ICustomizeQueryPanelProps) => {
    const { pageContext } = useComponentContext();
    const { initialQuery } = props;

    const [title, setTitle] = useObservable<string>(initialQuery.title);
    const originalBranchName = refToBranchName(initialQuery.targetRefName || "");
    const [branchName, setBranchName] = useObservable<string>(originalBranchName);
    const [showDraftState, setShowDraftState] = useObservable<boolean>(
        initialQuery.status !== PullRequestStatus.Completed && initialQuery.status !== PullRequestStatus.Abandoned
    );
    const [showGroupByVote, setShowGroupByVote] = useObservable<boolean>(initialQuery.status === PullRequestStatus.Active);
    const [showClosedFilter, setShowClosedFilter] = useObservable<boolean>(initialQuery.status !== PullRequestStatus.Active);
    const [groupByVote, setGroupByVote] = useObservable<boolean>(!!initialQuery.groupByVote);
    const [excludeDuplicates, setExcludeDuplicates] = useObservable<boolean>(!initialQuery.includeDuplicates);

    const [maxClosedDays, setMaxClosedDays] = useObservable<number | undefined>(initialQuery.maxDaysSinceClosed);
    const [maxCreatedDays, setMaxCreatedDays] = useObservable<number | undefined>(initialQuery.maxDaysSinceCreated);
    const [maxUpdatedDays, setMaxUpdatedDays] = useObservable<number | undefined>(initialQuery.maxDaysSinceLastUpdated);

    const statusSelection = new DropdownSelection();
    statusSelection.select(statusItems.findIndex(item => item.data === initialQuery.status));
    let status = initialQuery.status;

    const draftStateSelection = new DropdownSelection();
    draftStateSelection.select(draftStateItems.findIndex(item => item.data === initialQuery.draftState));
    let draftState = initialQuery.draftState;

    const initialReviewerIds = initialQuery.myTeamsAsReviewer ? props.myTeamIds : initialQuery.reviewerIds || [];

    let createdBy: string[] = initialQuery.authorIds || [];
    let assignedTo: string[] = initialReviewerIds;

    const [disabled, setDisabled] = useObservable<boolean>(true);

    const updateDisabled = () => {
        const newDisabledValue =
            (title.value === initialQuery.title &&
                status === initialQuery.status &&
                draftState === initialQuery.draftState &&
                arrayEquals(createdBy, initialQuery.authorIds || []) &&
                arrayEquals(assignedTo, initialReviewerIds || []) &&
                branchName.value === originalBranchName &&
                excludeDuplicates.value === !initialQuery.includeDuplicates &&
                (showClosedFilter ? maxClosedDays.value : undefined) === initialQuery.maxDaysSinceClosed &&
                maxCreatedDays.value === initialQuery.maxDaysSinceCreated &&
                maxUpdatedDays.value === initialQuery.maxDaysSinceLastUpdated &&
                (showGroupByVote.value ? groupByVote.value : false) === !!initialQuery.groupByVote) ||
            !title.value;

        if (disabled.value !== newDisabledValue) {
            setDisabled(newDisabledValue);
        }
    };

    const onClickSave = () => {
        const myTeamsAsReviewer = initialQuery.myTeamsAsReviewer && arrayEquals(assignedTo, initialReviewerIds);
        const reviewerIds = myTeamsAsReviewer ? undefined : assignedTo;
        props.onSave({
            authorIds: createdBy,
            draftState,
            groupByVote: status === PullRequestStatus.Active ? groupByVote.value : undefined,
            id: initialQuery.id,
            includeDuplicates: excludeDuplicates.value ? undefined : true,
            maxDaysSinceClosed: status !== PullRequestStatus.Active ? maxClosedDays.value : undefined,
            maxDaysSinceCreated: maxCreatedDays.value,
            maxDaysSinceLastUpdated: maxUpdatedDays.value,
            myTeamsAsReviewer,
            reviewerIds,
            status: status,
            targetRefName: branchName.value ? branchNameToRef(branchName.value) : undefined,
            title: title.value
        });
    };

    const onChangeTitle = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        setTitle(value);
        updateDisabled();
    };

    const onSelectStatus = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<PullRequestStatus>) => {
        status = item.data!;

        const hasDrafts = status !== PullRequestStatus.Completed && status !== PullRequestStatus.Abandoned;
        setShowDraftState(hasDrafts);
        if (!hasDrafts) {
            // Forget any previous draft filter if the dropdown doesn't apply
            draftState = undefined;
            draftStateSelection.select(0);
        }

        setShowGroupByVote(status === PullRequestStatus.Active);
        setShowClosedFilter(status !== PullRequestStatus.Active);
        updateDisabled();
    };

    const onSelectDraftState = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<boolean | undefined>) => {
        draftState = item.data!;
        updateDisabled();
    };

    const onChangeCreatedBy = (identities: string[]) => {
        createdBy = identities;
        updateDisabled();
    };

    const onChangeAssignedTo = (identities: string[]) => {
        assignedTo = identities;
        updateDisabled();
    };

    const onSelectBranch = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem>) => {
        setBranchName(item ? item.text! : "");
        updateDisabled();
    };

    const onBranchChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        setBranchName(value);
        updateDisabled();
    };

    const version = originalBranchName ? branchToVersionDescriptor(originalBranchName) : undefined;

    return (
        <Observer showOverlay={props.showOverlay}>
            {({ showOverlay }: { showOverlay: boolean }) => {
                return (
                    <CustomPanel
                        defaultActiveElement={initialQuery.readonly ? ".bolt-panel-footer-buttons button" : ".repos-prs-custom-title-textfield"}
                        lightDismiss={false}
                        onDismiss={props.onDismiss}
                    >
                        <PanelHeader
                            description={Resources.CustomizePanelDescription}
                            onDismiss={props.onDismiss}
                            titleProps={{ text: Resources.CustomizePanelTitle }}
                        />
                        <PanelContent>
                            <div className="flex-column flex-grow rhythm-vertical-20">
                                <FormItem label={Resources.Title}>
                                    <TextField
                                        autoSelect={true}
                                        disabled={initialQuery.readonly}
                                        inputClassName="repos-prs-custom-title-textfield"
                                        onChange={onChangeTitle}
                                        placeholder={Resources.Title}
                                        value={title}
                                    />
                                </FormItem>
                                {initialQuery.readonly ? (
                                    <div className="body-l">{Resources.BuiltInSectionMessage}</div>
                                ) : (
                                    <>
                                        <FormItem label={Resources.Status}>
                                            <Dropdown items={statusItems} onSelect={onSelectStatus} selection={statusSelection} />
                                        </FormItem>
                                        <Observer {...{ showDraftState }}>
                                            {() => {
                                                return (
                                                    showDraftState.value && (
                                                        <FormItem label={Resources.DraftState}>
                                                            <Dropdown
                                                                items={draftStateItems}
                                                                onSelect={onSelectDraftState}
                                                                selection={draftStateSelection}
                                                            />
                                                        </FormItem>
                                                    )
                                                );
                                            }}
                                        </Observer>
                                        <FormItem label={Resources.CreatedBy}>
                                            <PullRequestIdentityPicker
                                                initialIdentities={createdBy}
                                                onChanged={onChangeCreatedBy}
                                                peoplePickerProvider={props.usersOnlyProvider}
                                                placeholderText={Resources.SearchUsersPlaceholder}
                                            />
                                        </FormItem>
                                        <FormItem label={Resources.AssignedTo}>
                                            <PullRequestIdentityPicker
                                                initialIdentities={assignedTo}
                                                onChanged={onChangeAssignedTo}
                                                peoplePickerProvider={props.usersAndGroupsProvider}
                                                placeholderText={Resources.SearchUsersAndGroupsPlaceholder}
                                            />
                                        </FormItem>
                                        <FormItem label={Resources.TargetBranch}>
                                            {getGitRepository(pageContext) ? (
                                                <VersionDropdown
                                                    initiallySelectedVersion={version}
                                                    isDropdownFullWidth={true}
                                                    onSelect={onSelectBranch}
                                                    placeholder={Resources.TargetBranch}
                                                    providerOptions={{
                                                        viewClearButton: true,
                                                        viewTagsPivot: false
                                                    }}
                                                    renderExpandable={VersionDropdownExpandableTextField}
                                                />
                                            ) : (
                                                <TextField onChange={onBranchChanged} value={branchName} />
                                            )}
                                        </FormItem>
                                        <Observer
                                            maxClosedDays={maxClosedDays}
                                            maxCreatedDays={maxCreatedDays}
                                            maxUpdatedDays={maxUpdatedDays}
                                            showClosedFilter={showClosedFilter}
                                        >
                                            {() => {
                                                return (
                                                    <>
                                                        <DaysAgoCheckbox
                                                            daysAgoValue={maxCreatedDays}
                                                            format={Resources.DateFilterFormatCreated}
                                                            label={Resources.DateFilterLabelCreated}
                                                            setDaysAgoValue={setMaxCreatedDays}
                                                            updateDisabled={updateDisabled}
                                                        />
                                                        <DaysAgoCheckbox
                                                            daysAgoValue={maxUpdatedDays}
                                                            format={Resources.DateFilterFormatUpdated}
                                                            label={Resources.DateFilterLabelUpdated}
                                                            setDaysAgoValue={setMaxUpdatedDays}
                                                            updateDisabled={updateDisabled}
                                                        />
                                                        {showClosedFilter.value && (
                                                            <DaysAgoCheckbox
                                                                daysAgoValue={maxClosedDays}
                                                                format={Resources.DateFilterFormatCompleted}
                                                                label={Resources.DateFilterLabelCompleted}
                                                                setDaysAgoValue={setMaxClosedDays}
                                                                updateDisabled={updateDisabled}
                                                            />
                                                        )}
                                                    </>
                                                );
                                            }}
                                        </Observer>
                                        <Observer show={showGroupByVote}>
                                            {() => {
                                                return showGroupByVote.value ? (
                                                    <Checkbox
                                                        checked={groupByVote}
                                                        label={Resources.GroupByVote}
                                                        onChange={(event, checked) => {
                                                            setGroupByVote(checked);
                                                            updateDisabled();
                                                        }}
                                                    />
                                                ) : null;
                                            }}
                                        </Observer>
                                        <Checkbox
                                            checked={excludeDuplicates}
                                            label={Resources.ExcludeDuplicates}
                                            onChange={(event, checked) => {
                                                setExcludeDuplicates(checked);
                                                updateDisabled();
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </PanelContent>
                        <PanelFooter>
                            <ButtonGroup className="bolt-panel-footer-buttons flex-grow">
                                <Button key="cancel" onClick={props.onDismiss} text={Resources.Cancel} />
                                <Observer disabled={disabled}>
                                    {(props: { disabled: boolean }) => (
                                        <Button disabled={props.disabled} key="save" primary={true} onClick={onClickSave} text={Resources.Save} />
                                    )}
                                </Observer>
                            </ButtonGroup>
                        </PanelFooter>
                        {showOverlay && <PanelOverlay overlayContent={<Spinner />} />}
                    </CustomPanel>
                );
            }}
        </Observer>
    );
};

interface IPullRequestIdentityPickerProps {
    initialIdentities: string[];
    onChanged: (ids: string[]) => void;
    peoplePickerProvider: IPeoplePickerProvider;
    placeholderText: string;
}

const PullRequestIdentityPicker = (props: IPullRequestIdentityPickerProps) => {
    const identities = new ObservableArray<IIdentity>([]);

    if (props.initialIdentities.length) {
        (props.peoplePickerProvider.getEntitiesFromUniqueAttribute!(props.initialIdentities) as PromiseLike<IIdentity[]>).then(newIdentities =>
            identities.push(...newIdentities)
        );
    }

    const onIdentityAdded = (identity: IIdentity) => {
        identities.push(identity);
        props.onChanged(identities.value.filter(id => id.localId).map(id => id.localId!));
    };

    const onIdentityRemoved = (identity: IIdentity) => {
        identities.splice(identities.value.indexOf(identity), 1);
        props.onChanged(identities.value.filter(id => id.localId).map(id => id.localId!));
    };

    const onIdentitiesRemoved = (identitiesRemoved: IIdentity[]) => {
        // Could probably optimize this to created a dictionary out of the identities to remove,
        // keyed by id, but only worth it if we foresee a large number of identities in either
        // of these lists.

        identities.removeAll(identity => identitiesRemoved.indexOf(identity) !== -1);
        props.onChanged(identities.value.filter(id => id.localId).map(id => id.localId!));
    };

    return (
        <IdentityPicker
            onIdentityAdded={onIdentityAdded}
            onIdentityRemoved={onIdentityRemoved}
            onIdentitiesRemoved={onIdentitiesRemoved}
            pickerProvider={props.peoplePickerProvider}
            selectedIdentities={identities}
        />
    );
};

interface IDaysAgoCheckboxProps {
    daysAgoValue: IReadonlyObservableValue<number | undefined>;
    format: string;
    label: string;
    setDaysAgoValue: (newValue: number | undefined) => void;
    updateDisabled: () => void;
}

const DaysAgoCheckbox = (props: IDaysAgoCheckboxProps) => {
    const { daysAgoValue, format, label, setDaysAgoValue, updateDisabled } = props;
    const daysAgoText = new ObservableValue(daysAgoValue.value === undefined ? "30" : daysAgoValue.value.toString());

    return (
        <div className="flex-row flex-center">
            <Checkbox
                checked={daysAgoValue.value !== undefined}
                className="flex-noshrink labelled"
                ariaLabel={label}
                onChange={(event, checked) => {
                    setDaysAgoValue(checked ? 30 : undefined);
                    updateDisabled();
                }}
            />
            <FormatComponent className="repos-prs-query-date-filter-label" format={format}>
                <TextField
                    containerClassName="repos-prs-query-date-filter-textbox"
                    disabled={daysAgoValue.value === undefined}
                    maxLength={5}
                    value={daysAgoText}
                    onChange={(event, value) => {
                        if (!value) {
                            daysAgoText.value = "";
                        } else {
                            const parsedValue = parseFloat(value);
                            if (!isNaN(parsedValue)) {
                                if (parsedValue !== daysAgoValue.value) {
                                    setDaysAgoValue(parsedValue);
                                    updateDisabled();
                                } else {
                                    daysAgoText.value = value;
                                }
                            }
                        }
                    }}
                />
            </FormatComponent>
        </div>
    );
};
